import { fieldsOf, report, whole, type Place } from "./checks.ts";
import type { CreatureLevel, Level, RulesProblem, XpGapRow } from "./types.ts";

/**
 * The records and cross-table checks of `creature_levels` and `xp_gap` (ADR-082's amendment of 2026-10-15, item 2, and
 * ADR-083's note of the same date): what a creature level and a row of the XP gap table hold, and that each table
 * climbs, falls and ends where the rules say.
 */

/**
 * The percent a template's own values are at level 1, and the least any creature level may hold.
 */
const BASE_PERCENT = 100;

/**
 * The most a creature level's percent may be: a thousand times the template's value.
 */
export const MAX_CREATURE_PERCENT = 100_000;

/**
 * A row that passed its checks, with the name its problems carry.
 */
export interface NamedRow<T> {
  readonly name: string;
  readonly record: T;
}

/**
 * Checks a creature level: its number and its health and XP percents, each from 100 to `MAX_CREATURE_PERCENT`. How the
 * rows climb is `checkCreatureLevels`.
 *
 * @param place Where to report.
 * @param record The record.
 * @returns The creature level, or null.
 */
export function creatureLevelOf(place: Place, record: Readonly<Record<string, unknown>>): CreatureLevel | null {
  const fields = fieldsOf(place, record, "", ["level", "healthPercent", "xpPercent"], "a creature level") as Readonly<Record<string, unknown>>;
  const level = whole(place, fields["level"], "level", 1);
  const healthPercent = whole(place, fields["healthPercent"], "healthPercent", BASE_PERCENT, MAX_CREATURE_PERCENT);
  const xpPercent = whole(place, fields["xpPercent"], "xpPercent", BASE_PERCENT, MAX_CREATURE_PERCENT);
  return level === null || healthPercent === null || xpPercent === null || Object.keys(fields).length !== 3 ? null : { level, healthPercent, xpPercent };
}

/**
 * Checks a row of the XP gap table: the levels the earner stands above the creature from which the row applies, and the
 * whole percent of a kill's XP it pays, 0 to 100. How the rows run is `checkXpGap`.
 *
 * @param place Where to report.
 * @param record The record.
 * @returns The row, or null.
 */
export function xpGapOf(place: Place, record: Readonly<Record<string, unknown>>): XpGapRow | null {
  const fields = fieldsOf(place, record, "", ["minGap", "percent"], "an XP gap row") as Readonly<Record<string, unknown>>;
  const minGap = whole(place, fields["minGap"], "minGap", 0);
  const percent = whole(place, fields["percent"], "percent", 0, BASE_PERCENT);
  return minGap === null || percent === null || Object.keys(fields).length !== 2 ? null : { minGap, percent };
}

/**
 * Reports a creature table that does not run as ADR-082's amendment has it: level 1 at the template's own 100 for both
 * columns, neither percent falling from one level to the next, and the last level at least the player table's last (ADR-083's amendment of 2026-10-21). Only rows that
 * passed their own checks are compared, each with the one before it among them.
 *
 * @param creatureLevels The creature levels that passed their checks, in the table's order.
 * @param levels The player levels that passed their checks.
 * @param problems Where problems go.
 */
export function checkCreatureLevels(creatureLevels: readonly NamedRow<CreatureLevel>[], levels: readonly Level[], problems: RulesProblem[]): void {
  let previous: CreatureLevel | null = null;
  for (const { name, record } of creatureLevels) {
    const place = { table: "creature_levels" as const, id: name, problems };
    if (record.level === 1) {
      for (const column of ["healthPercent", "xpPercent"] as const) {
        if (record[column] !== BASE_PERCENT) {
          report(place, `level 1's ${column} is ${record[column]}; level 1 is the template's own, ${BASE_PERCENT}`);
        }
      }
    }
    if (previous !== null) {
      for (const column of ["healthPercent", "xpPercent"] as const) {
        if (record[column] < previous[column]) {
          report(place, `level ${record.level}'s ${column} ${record[column]} is below level ${previous.level}'s ${previous[column]}; it never falls`);
        }
      }
    }
    previous = record;
  }
  const last = creatureLevels[creatureLevels.length - 1]?.record.level;
  const cap = levels[levels.length - 1]?.level;
  if (last !== undefined && cap !== undefined && last < cap) {
    report({ table: "creature_levels", id: null, problems }, `the last level is ${last}; it is below the levels table's last, ${cap}`);
  }
}

/**
 * Reports an XP gap table that does not run as ADR-083's note has it: a first row from gap 0 paying 100, each row from a
 * larger gap than the one before it and paying no more. Only rows that passed their own checks are compared.
 *
 * @param rows The rows that passed their checks, in the table's order, each with its name.
 * @param problems Where problems go.
 */
export function checkXpGap(rows: readonly NamedRow<XpGapRow>[], problems: RulesProblem[]): void {
  let previous: XpGapRow | null = null;
  for (const { name, record } of rows) {
    const place = { table: "xp_gap" as const, id: name, problems };
    if (previous === null) {
      if (record.minGap !== 0) {
        report(place, `the first row starts at gap ${record.minGap}; it starts at 0`);
      }
      if (record.percent !== BASE_PERCENT) {
        report(place, `the first row gives ${record.percent} percent; it gives ${BASE_PERCENT}`);
      }
    } else {
      if (record.minGap <= previous.minGap) {
        report(place, `minGap ${record.minGap} is not above the row before it, ${previous.minGap}; the rows run from smaller gaps to larger`);
      }
      if (record.percent > previous.percent) {
        report(place, `percent ${record.percent} is above the row before it, ${previous.percent}; the percent never rises`);
      }
    }
    previous = record;
  }
}
